import type { NavBarConfig } from '@gm-commercial/navbar-model';
import { type Profile, Role } from '@gm-commercial/profile-model';
import type { Dispatch } from 'redux';

import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { APP_ROUTES_CONFIG_MAPPING } from '~/common/configs/route.config';
import { SessionConfigType } from '~/common/constants/common.constant';
import {
  FEEDBACK_LOOP_FLAG,
  type FeatureFlagValueType,
} from '~/common/models/featureFlags.model';
import { SUPPORT_CONTACT_PATH } from '~/common/models/pages/supportPages.model';
import { RouteName } from '~/common/models/route.model';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';
import {
  ASSET_REPORT_FILTER_EMPTY_STATE,
  setAssetSessionConfig,
} from '~/features/assets/assetsSlice';
import { setOrgSessionConfig } from '~/features/organizations/organizationsSlice';
import AssetsIcon from '../../../assets/icons/leftMenu/assetsIcon.svg?react';
import HelpIcon from '../../../assets/icons/leftMenu/helpIcon.svg?react';
import OrganizationIcon from '../../../assets/icons/leftMenu/organizationIcon.svg?react';
import UserProfileIcon from '../../../assets/icons/leftMenu/userProfileIcon.svg?react';
import AssetsIconNew from '../../../assets/icons/new/assetsIcon.svg?react';
import FeedbackIcon from '../../../assets/icons/new/feedback.svg?react';
import type { NAV_DRAWER_MESSAGES } from '../GlobalNavDrawer.messages';
import type { NavListItem } from '../GlobalNavDrawer.model';

/**
 * Type guard to distinguish between NavListItem and NavBarItem
 * @param item either a NavListItem or NavBarItem
 * @returns `true` if item contains props specific to NavListItem, `false` otherwise
 */
export const isNavListItem = (item: unknown): item is NavListItem => {
  const test = item as NavListItem;
  return (
    test.routeName !== undefined ||
    test.excludedRoles !== undefined ||
    test.requiredRoles !== undefined ||
    test.permissions !== undefined
  );
};

export type LegacyNavDrawerProps = Omit<NavBarConfig, 'items'> & {
  items: NavListItem[];
};

export enum LegacyPath {
  DASHBOARD = 'dashboard',
  ORGANIZATIONS = 'organizations',
  USERS = 'users',
  ASSET_LIST = 'assetReport',
}

export const getLegacyNavDrawerConfig = ({
  messages,
  profileParams,
  dispatch,
  sessionId,
  findFlagScope,
}: {
  messages: typeof NAV_DRAWER_MESSAGES.legacyMenu;
  profileParams: Profile['permissionsContextIds'];
  dispatch?: Dispatch;
  sessionId?: string;
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
}): LegacyNavDrawerProps => {
  const context = {
    fleetsId: profileParams?.fleetsId || undefined,
    hubsId: profileParams?.hubsId || undefined,
    organizationsId: profileParams?.organizationsId || undefined,
  };
  const isFeedbackLoopEnabled = Boolean(findFlagScope?.(FEEDBACK_LOOP_FLAG));
  const legacyNavDrawerConfig = {
    homePath: '/',
    items: [
      {
        icon: <OrganizationIcon data-testid="org-list" />,
        path: '/organizations',
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS].authorizedPerms,
        requiredRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS].authorizedRoles,
        label: messages['common:organization_plural'],
        testId: 'organization-nav-list-item',
        callback: () => {
          if (dispatch && sessionId) {
            //resetting the tab and token as user land on Organization list page via nav drawer
            dispatch(
              setOrgSessionConfig({
                [SessionConfigType.LIST_VIEW]: {
                  [sessionId]: {
                    page: 0,
                    continuationToken: null,
                    continuationCache: undefined,
                    filterCriteria: undefined,
                    searchCriteria: undefined,
                  },
                },
              })
            );
          }
        },
      },
      {
        icon: <OrganizationIcon data-testid="org-details" />,
        path: makeRoutePath(
          findRoute(RouteName.ORGANIZATIONS_DETAILS),
          context
        ),
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS_DETAILS]
            .authorizedPerms,
        requiredRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS_DETAILS]
            .authorizedRoles,
        label: messages['common:organization'],
        testId: 'org-details-nav-list-item',
      },
      {
        icon: <OrganizationIcon data-testid="fleet-details" />,
        path: makeRoutePath(
          findRoute(RouteName.ORGANIZATIONS_FLEETS_DETAILS),
          context
        ),
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS_FLEETS_DETAILS]
            .authorizedPerms,
        requiredRoles: [Role.FLEET_MANAGER],
        label: messages['common:fleet'],
        testId: 'fleet-details-nav-list-item',
      },
      {
        icon: <AssetsIcon style={{ width: '25px' }} />,
        path: makeRoutePath(findRoute(RouteName.ADVISOR_VEHICLE_LIST), context),
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ADVISOR_VEHICLE_LIST]
            .authorizedPerms,
        label: messages['common:asset_plural'],
        callback: () => {
          if (dispatch && sessionId) {
            //resetting the tab and token as user land on Vehicle list page via nav drawer
            dispatch(
              setAssetSessionConfig({
                [SessionConfigType.LIST_VIEW]: {
                  [sessionId]: {
                    page: 0,
                    continuationAdvisorVehicleCache: undefined,
                    continuationToken: null,
                    filterType: ASSET_REPORT_FILTER_EMPTY_STATE,
                    searchCriteria: undefined,
                  },
                },
              })
            );
          }
        },
        testId: 'asset-list-nav-list-item',
      },
      {
        icon: <UserProfileIcon />,
        path: makeRoutePath(findRoute(RouteName.ORGANIZATIONS_USERS), context),
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS_USERS]
            .authorizedPerms,
        excludedRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.ORGANIZATIONS_USERS]
            .excludedRoles,
        label: messages['common:user_plural'],
        testId: 'user-list-nav-list-item',
      },
      {
        icon: <AssetsIconNew className="noFill" data-testid="devices-list" />,
        path: '/peripherals',
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.CS_PLATFORM_PERIPHERALS_LIST]
            .authorizedPerms,
        requiredRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.CS_PLATFORM_PERIPHERALS_LIST]
            .authorizedRoles,
        label: messages['common:device_plural'],
        testId: 'devices-nav-list-item',
      },
      {
        icon: (
          <FeedbackIcon
            className="noFill"
            data-testid="feedback-list"
            width={20}
            height={20}
          />
        ),
        path: isFeedbackLoopEnabled
          ? makeRoutePath(findRoute(RouteName.CS_PLATFORM_FEEDBACK), context)
          : '',
        permissions:
          APP_ROUTES_CONFIG_MAPPING[RouteName.CS_PLATFORM_FEEDBACK]
            .authorizedPerms,
        requiredRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.CS_PLATFORM_FEEDBACK]
            .authorizedRoles,
        excludedRoles:
          APP_ROUTES_CONFIG_MAPPING[RouteName.CS_PLATFORM_FEEDBACK]
            .excludedRoles,
        label: translateText('feedback:feedback'),
        testId: 'feedback-nav-list-item',
      },
      {
        label: messages['common:footer.support'],
        icon: <HelpIcon />,
        testId: 'support-nav-list-item',
        path: SUPPORT_CONTACT_PATH,
        permissions: [],
        excludedRoles: [Role.SYSTEM_MANAGER, Role.SYSTEM_READER],
      },
    ],
  };
  return {
    ...legacyNavDrawerConfig,
    items: legacyNavDrawerConfig.items.filter((item) => item.path !== ''),
  };
};
